
import { createClient } from '@supabase/supabase-js';
import { BackendService } from './backendInterface';
import { 
  User, UserRole, Product, Order, OrderStatus, Invoice, 
  Testimonial, NewCustomerData, NewProductData, NewOrderData, NewTestimonialData, OrderItem, Notification, NotificationType, Inquiry, SiteContent, Category,
  InventoryItem, InventoryUsageLog, ReorderRule, OrderEvent, Shipment, PaymentTransaction, CustomerBranch, ProductBranchPrice, NewCustomerBranchData
} from '../types';
import { getInvoicePayableAmount } from './invoiceUtils';

const REQUIRED_TABLES = [
  'profiles',
  'customer_branches',
  'categories',
  'products',
  'product_branch_prices',
  'orders',
  'order_items',
  'order_approvals',
  'order_events',
  'shipments',
  'invoices',
  'payment_transactions',
  'payment_webhook_events',
  'inventory_items',
  'inventory_usage_logs',
  'inventory_reorder_rules',
  'testimonials',
  'notifications',
  'inquiries',
  'site_content'
] as const;

type DatabaseOrderItemRow = {
  id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  price_per_unit: number;
  variation_id?: string | null;
  variation_name?: string | null;
  packed?: boolean | null;
  received_quantity?: number | null;
  receive_remark?: string | null;
};

type DatabaseShipmentRow = {
  id: number;
  order_id: number;
  tracking_number: string;
  courier_partner?: string | null;
  status: 'SHIPPED' | 'DELIVERED';
  shipped_by?: string | number | null;
  shipped_at: string;
  delivered_by?: string | number | null;
  delivered_at?: string | null;
};

type DatabaseOrderRow = {
  id: number;
  customer_id: number | string;
  customer_name: string;
  branch_id?: number | null;
  branch_name?: string | null;
  billing_address?: string | null;
  shipping_address?: string | null;
  gst_number?: string | null;
  status: OrderStatus | string;
  total_amount: number;
  created_at: string;
  customer_admin_id?: number | string | null;
  submitted_by?: number | string | null;
  approved_by?: number | string | null;
  approved_at?: string | null;
  denied_by?: number | string | null;
  denied_at?: string | null;
  approval_remark?: string | null;
  order_items?: DatabaseOrderItemRow[];
};

type OrderCreatePayload = NewOrderData & {
  submittedById?: number | string | null;
  customerAdminId?: number | string | null;
};

/* 
  ================================================================================
  SUPABASE DATABASE SETUP SCRIPT (SECURED & OPTIMIZED)
  ================================================================================
*/
export const SQL_SETUP_SCRIPT = `
-- Enable UUID extension
create extension if not exists "uuid-ossp";

-- 1. PROFILES (Users)
create table if not exists profiles (
  id uuid references auth.users not null primary key,
  email text,
  name text,
  role text default 'CUSTOMER',
  phone text,
  billing_address text,
  shipping_address text,
  gst_number text,
  is_approved boolean default false,
  created_at timestamptz default now()
);
alter table profiles add column if not exists organization_admin_id uuid references profiles(id);
alter table profiles add column if not exists branch_id bigint references customer_branches(id) on delete set null;
create index if not exists profiles_branch_id_idx on profiles(branch_id);
alter table profiles drop constraint if exists profiles_role_check;
alter table profiles add constraint profiles_role_check
  check (role in ('ADMIN', 'SUPER_ADMIN', 'MANAGER', 'ACCOUNTANT', 'CUSTOMER_ADMIN', 'CUSTOMER_BRANCH_ADMIN', 'CUSTOMER_MANAGER', 'CUSTOMER'));

-- 1.2 CUSTOMER BRANCHES
create table if not exists customer_branches (
  id bigint generated by default as identity primary key,
  organization_admin_id uuid references profiles(id) on delete cascade,
  name text not null,
  billing_address text not null,
  shipping_address text not null,
  gst_number text,
  is_default boolean default false,
  created_at timestamptz default now(),
  updated_at timestamptz default now(),
  unique (organization_admin_id, name)
);
alter table customer_branches add column if not exists organization_admin_id uuid references profiles(id) on delete cascade;
alter table customer_branches add column if not exists name text;
alter table customer_branches add column if not exists billing_address text;
alter table customer_branches add column if not exists shipping_address text;
alter table customer_branches add column if not exists gst_number text;
alter table customer_branches add column if not exists is_default boolean default false;
alter table customer_branches add column if not exists created_at timestamptz default now();
alter table customer_branches add column if not exists updated_at timestamptz default now();
update customer_branches set is_default = false where is_default is null;

-- 1.5 CATEGORIES
create table if not exists categories (
  id bigint generated by default as identity primary key,
  name text not null,
  parent_id bigint references categories(id),
  created_at timestamptz default now()
);

-- 2. PRODUCTS
create table if not exists products (
  id bigint generated by default as identity primary key,
  name text not null,
  description text,
  image_url text,
  hsn_code text,
  stock_quantity integer default 0,
  base_price numeric default 0,
  customer_prices jsonb default '{}'::jsonb,
  customer_variation_prices jsonb default '{}'::jsonb,
  category_id bigint references categories(id),
  subcategory_id bigint references categories(id),
  variations jsonb default '[]'::jsonb,
  created_at timestamptz default now()
);

alter table products add column if not exists hsn_code text;

-- 2.5 PRODUCT PRICES BY BRANCH
create table if not exists product_branch_prices (
  id bigint generated by default as identity primary key,
  organization_admin_id uuid references profiles(id) on delete cascade,
  branch_id bigint references customer_branches(id) on delete cascade,
  product_id bigint references products(id) on delete cascade,
  variation_id text,
  price numeric not null default 0,
  created_at timestamptz default now(),
  updated_at timestamptz default now(),
  unique (branch_id, product_id, variation_id)
);
alter table product_branch_prices add column if not exists organization_admin_id uuid references profiles(id) on delete cascade;
alter table product_branch_prices add column if not exists branch_id bigint references customer_branches(id) on delete cascade;
alter table product_branch_prices add column if not exists product_id bigint references products(id) on delete cascade;
alter table product_branch_prices add column if not exists variation_id text;
alter table product_branch_prices add column if not exists price numeric not null default 0;
alter table product_branch_prices add column if not exists created_at timestamptz default now();
alter table product_branch_prices add column if not exists updated_at timestamptz default now();

-- 3. ORDERS
create table if not exists orders (
  id bigint generated by default as identity primary key,
  customer_id uuid references profiles(id),
  customer_name text,
  branch_id bigint references customer_branches(id) on delete set null,
  branch_name text,
  billing_address text,
  shipping_address text,
  gst_number text,
  status text default 'PENDING',
  customer_admin_id uuid references profiles(id),
  submitted_by uuid references profiles(id),
  approved_by uuid references profiles(id),
  approved_at timestamptz,
  denied_by uuid references profiles(id),
  denied_at timestamptz,
  approval_remark text,
  total_amount numeric default 0,
  created_at timestamptz default now()
);
alter table orders add column if not exists customer_admin_id uuid references profiles(id);
alter table orders add column if not exists submitted_by uuid references profiles(id);
alter table orders add column if not exists approved_by uuid references profiles(id);
alter table orders add column if not exists approved_at timestamptz;
alter table orders add column if not exists denied_by uuid references profiles(id);
alter table orders add column if not exists denied_at timestamptz;
alter table orders add column if not exists approval_remark text;
alter table orders add column if not exists branch_id bigint references customer_branches(id) on delete set null;
alter table orders add column if not exists branch_name text;
alter table orders add column if not exists billing_address text;
alter table orders add column if not exists shipping_address text;
alter table orders add column if not exists gst_number text;
alter table orders drop constraint if exists orders_status_check;
alter table orders add constraint orders_status_check
  check (status in ('PENDING_CUSTOMER_APPROVAL', 'CUSTOMER_DENIED', 'PENDING', 'ACCEPTED', 'APPROVED', 'SHIPPED', 'COMPLETED', 'ISSUE_REPORTED', 'PAID'));

-- 4. ORDER ITEMS
create table if not exists order_items (
  id bigint generated by default as identity primary key,
  order_id bigint references orders(id) on delete cascade,
  product_id bigint references products(id) on delete set null,
  product_name text,
  quantity integer,
  price_per_unit numeric,
  variation_id text,
  variation_name text,
  packed boolean default false,
  received_quantity integer default 0,
  receive_remark text
);

alter table order_items add column if not exists packed boolean default false;
alter table order_items add column if not exists received_quantity integer default 0;
alter table order_items add column if not exists receive_remark text;

-- 4.5 ORDER APPROVAL DECISIONS
create table if not exists order_approvals (
  id bigint generated by default as identity primary key,
  order_id bigint references orders(id) on delete cascade,
  actor_id uuid references profiles(id),
  actor_name text,
  decision text not null,
  remark text,
  created_at timestamptz default now()
);

-- 4.6 ORDER EVENTS (AUDIT TRAIL)
create table if not exists order_events (
  id bigint generated by default as identity primary key,
  order_id bigint references orders(id) on delete cascade,
  event_type text not null,
  actor_id uuid references profiles(id),
  actor_name text,
  from_status text,
  to_status text,
  details text,
  created_at timestamptz default now()
);

-- 4.7 SHIPMENTS
create table if not exists shipments (
  id bigint generated by default as identity primary key,
  order_id bigint unique references orders(id) on delete cascade,
  tracking_number text not null,
  courier_partner text,
  status text default 'SHIPPED',
  shipped_by uuid references profiles(id),
  shipped_at timestamptz default now(),
  delivered_by uuid references profiles(id),
  delivered_at timestamptz
);

-- 5. INVOICES
create table if not exists invoices (
  id bigint generated by default as identity primary key,
  order_id bigint references orders(id),
  customer_id uuid references profiles(id),
  customer_name text,
  amount numeric,
  tax_rate numeric default 0,
  notes text,
  status text default 'Unpaid',
  issued_at timestamptz default now(),
  due_date timestamptz
);

-- 5.5 PAYMENT TRANSACTIONS (GATEWAY + RECONCILIATION)
create table if not exists payment_transactions (
  id bigint generated by default as identity primary key,
  invoice_id bigint references invoices(id) on delete cascade,
  order_id bigint references orders(id) on delete cascade,
  amount numeric not null default 0,
  provider text not null default 'SIMULATED_GATEWAY',
  gateway_transaction_id text,
  status text not null default 'INITIATED',
  payload jsonb,
  reconciled_at timestamptz,
  created_by uuid references profiles(id),
  created_at timestamptz default now(),
  updated_at timestamptz default now()
);
alter table payment_transactions drop constraint if exists payment_transactions_status_check;
alter table payment_transactions add constraint payment_transactions_status_check
  check (status in ('INITIATED', 'SUCCESS', 'FAILED', 'RECONCILED', 'REFUND_PENDING', 'REFUNDED', 'REFUND_FAILED'));

create table if not exists payment_webhook_events (
  id bigint generated by default as identity primary key,
  provider text not null default 'RAZORPAY',
  event_id text not null,
  event_type text not null,
  invoice_id bigint references invoices(id) on delete set null,
  order_id bigint references orders(id) on delete set null,
  payload jsonb,
  processed_at timestamptz default now(),
  unique (provider, event_id)
);

-- 5.6 PERSISTENT INVENTORY BY CUSTOMER ORG
create table if not exists inventory_items (
  id bigint generated by default as identity primary key,
  organization_admin_id uuid references profiles(id),
  branch_id bigint references customer_branches(id) on delete cascade,
  product_id bigint references products(id) on delete set null,
  product_name text,
  variation_id text,
  variation_name text,
  quantity integer not null default 0,
  created_at timestamptz default now(),
  updated_at timestamptz default now(),
  unique (organization_admin_id, branch_id, product_id, variation_id)
);
alter table inventory_items add column if not exists branch_id bigint references customer_branches(id) on delete cascade;
alter table inventory_items drop constraint if exists inventory_items_organization_admin_id_product_id_variation_id_key;
alter table inventory_items drop constraint if exists inventory_items_branch_id_product_id_variation_id_key;
alter table inventory_items drop constraint if exists inventory_items_branch_key;
drop index if exists inventory_items_org_product_variation_idx;
drop index if exists public.inventory_items_org_product_variation_idx;
alter table inventory_items add constraint inventory_items_branch_key unique (organization_admin_id, branch_id, product_id, variation_id);

create table if not exists inventory_usage_logs (
  id bigint generated by default as identity primary key,
  organization_admin_id uuid references profiles(id),
  branch_id bigint references customer_branches(id) on delete cascade,
  inventory_item_id bigint references inventory_items(id) on delete cascade,
  product_id bigint references products(id) on delete set null,
  product_name text,
  variation_id text,
  variation_name text,
  quantity integer not null default 0,
  note text,
  created_by uuid references profiles(id) on delete set null,
  created_by_name text,
  created_at timestamptz default now()
);
alter table inventory_usage_logs add column if not exists organization_admin_id uuid references profiles(id);
alter table inventory_usage_logs add column if not exists branch_id bigint references customer_branches(id) on delete cascade;
alter table inventory_usage_logs add column if not exists inventory_item_id bigint references inventory_items(id) on delete cascade;
alter table inventory_usage_logs add column if not exists product_id bigint references products(id) on delete set null;
alter table inventory_usage_logs add column if not exists product_name text;
alter table inventory_usage_logs add column if not exists variation_id text;
alter table inventory_usage_logs add column if not exists variation_name text;
alter table inventory_usage_logs add column if not exists quantity integer;
alter table inventory_usage_logs add column if not exists note text;
alter table inventory_usage_logs add column if not exists created_by uuid references profiles(id) on delete set null;
alter table inventory_usage_logs add column if not exists created_by_name text;
alter table inventory_usage_logs add column if not exists created_at timestamptz default now();

create table if not exists inventory_reorder_rules (
  id bigint generated by default as identity primary key,
  organization_admin_id uuid references profiles(id),
  branch_id bigint references customer_branches(id) on delete cascade,
  product_id bigint references products(id) on delete cascade,
  variation_id text,
  threshold integer not null default 10,
  created_at timestamptz default now(),
  updated_at timestamptz default now(),
  unique (organization_admin_id, branch_id, product_id, variation_id)
);
alter table inventory_reorder_rules add column if not exists branch_id bigint references customer_branches(id) on delete cascade;
alter table inventory_reorder_rules drop constraint if exists inventory_reorder_rules_organization_admin_id_product_id_variati_key;
alter table inventory_reorder_rules drop constraint if exists inventory_reorder_rules_branch_id_product_id_variation_id_key;
alter table inventory_reorder_rules drop constraint if exists inventory_reorder_rules_branch_key;
drop index if exists inventory_reorder_rules_org_product_variation_idx;
drop index if exists public.inventory_reorder_rules_org_product_variation_idx;
alter table inventory_reorder_rules add constraint inventory_reorder_rules_branch_key unique (organization_admin_id, branch_id, product_id, variation_id);

-- 6. TESTIMONIALS
create table if not exists testimonials (
  id bigint generated by default as identity primary key,
  customer_id uuid references profiles(id),
  customer_name text,
  product_id bigint references products(id) on delete set null,
  product_name text,
  rating integer,
  comment text,
  created_at timestamptz default now()
);

-- 7. NOTIFICATIONS
create table if not exists notifications (
  id bigint generated by default as identity primary key,
  user_id uuid references profiles(id),
  title text,
  message text,
  type text,
  external_key text,
  entity_type text,
  entity_id bigint,
  metadata jsonb,
  is_read boolean default false,
  created_at timestamptz default now()
);
alter table notifications add column if not exists title text;
alter table notifications add column if not exists external_key text;
alter table notifications add column if not exists entity_type text;
alter table notifications add column if not exists entity_id bigint;
alter table notifications add column if not exists metadata jsonb;
alter table notifications drop constraint if exists notifications_type_check;
alter table notifications add constraint notifications_type_check
  check (type in ('success', 'error', 'info'));
create unique index if not exists notifications_user_external_key_idx
  on notifications (user_id, external_key)
  where external_key is not null;

-- 8. INQUIRIES
create table if not exists inquiries (
  id bigint generated by default as identity primary key,
  name text,
  email text,
  company text,
  message text,
  status text default 'New',
  created_at timestamptz default now()
);

-- 9. SITE CONTENT (CMS)
create table if not exists site_content (
  key text primary key,
  value jsonb,
  section text
);

-- 10. SECURE PL/PGSQL FUNCTIONS

-- Check Setup Status (full schema)
create or replace function public.missing_required_tables()
returns text[]
language plpgsql
security definer
set search_path = public
as $$
declare
  required_tables text[] := array[
    ${REQUIRED_TABLES.map((table) => `'${table}'`).join(',\n    ')}
  ];
  missing_tables text[];
begin
  select coalesce(array_agg(t), array[]::text[])
    into missing_tables
  from unnest(required_tables) as t
  where not exists (
    select 1
    from information_schema.tables it
    where it.table_schema = 'public'
      and it.table_name = t
  );
  return missing_tables;
end;
$$;

create or replace function public.is_db_setup()
returns boolean 
language plpgsql 
security definer 
set search_path = public
as $$
begin
  return cardinality(public.missing_required_tables()) = 0;
end;
$$;

-- Promote User to Admin
create or replace function public.promote_to_admin(target_email text)
returns text
language plpgsql
security definer
set search_path = public
as $$
begin
  update public.profiles
  set role = 'ADMIN', is_approved = true
  where email = target_email;
  
  if found then
    return 'Success: ' || target_email || ' is now an administrator.';
  else
    return 'Error: User with email ' || target_email || ' not found in profiles.';
  end if;
end;
$$;

create or replace function public.is_admin_user(target_user uuid)
returns boolean
language plpgsql
security definer
set search_path = public
as $$
begin
  return exists (
    select 1
    from public.profiles p
    where p.id = target_user
      and p.role in ('ADMIN', 'SUPER_ADMIN', 'MANAGER', 'ACCOUNTANT')
  );
end;
$$;

create or replace function public.is_customer_org_member(org_admin uuid, target_user uuid default auth.uid())
returns boolean
language plpgsql
security definer
set search_path = public
as $$
begin
  if org_admin is null or target_user is null then
    return false;
  end if;
  return exists (
    select 1
    from public.profiles p
    where p.id = target_user
      and (
        p.id = org_admin
        or p.organization_admin_id = org_admin
      )
  );
end;
$$;

create or replace function public.can_access_customer_record(record_org_admin uuid, record_branch_id bigint, target_user uuid default auth.uid())
returns boolean
language plpgsql
security definer
set search_path = public
as $$
declare
  actor public.profiles%rowtype;
begin
  if record_org_admin is null or target_user is null then
    return false;
  end if;

  select *
  into actor
  from public.profiles
  where id = target_user;

  if actor.id is null then
    return false;
  end if;

  if actor.id <> record_org_admin and actor.organization_admin_id <> record_org_admin then
    return false;
  end if;

  if actor.id = record_org_admin then
    return true;
  end if;

  if actor.branch_id is null then
    return true;
  end if;

  return coalesce(record_branch_id = actor.branch_id, false);
end;
$$;

create or replace function public.can_access_customer_profile(profile_id uuid, profile_org_admin uuid, profile_branch_id bigint, target_user uuid default auth.uid())
returns boolean
language plpgsql
security definer
set search_path = public
as $$
declare
  actor public.profiles%rowtype;
begin
  if target_user is null then
    return false;
  end if;

  if profile_id = target_user then
    return true;
  end if;

  if profile_org_admin is null then
    return false;
  end if;

  select *
  into actor
  from public.profiles
  where id = target_user;

  if actor.id is null then
    return false;
  end if;

  if actor.id <> profile_org_admin and actor.organization_admin_id <> profile_org_admin then
    return false;
  end if;

  if actor.id = profile_org_admin then
    return true;
  end if;

  if actor.branch_id is null then
    return true;
  end if;

  if profile_id = profile_org_admin then
    return true;
  end if;

  if profile_branch_id is null then
    return false;
  end if;

  return coalesce(profile_branch_id = actor.branch_id, false);
end;
$$;

-- Handle New User Registration (Updated to include Address & GST)
create or replace function public.handle_new_user()
returns trigger
language plpgsql
security definer
set search_path = public
as $$
begin
  insert into public.profiles (
    id, email, name, role, phone, gst_number, billing_address, shipping_address, is_approved, organization_admin_id
  )
  values (
    new.id, 
    new.email, 
    coalesce(new.raw_user_meta_data->>'name', ''),
    coalesce(new.raw_user_meta_data->>'role', 'CUSTOMER_ADMIN'),
    new.raw_user_meta_data->>'phone',
    new.raw_user_meta_data->>'gst_number',
    new.raw_user_meta_data->>'billing_address',
    new.raw_user_meta_data->>'shipping_address',
    case when coalesce(new.raw_user_meta_data->>'role', 'CUSTOMER_ADMIN') = 'ADMIN' then true else false end,
    case
      when coalesce(new.raw_user_meta_data->>'role', 'CUSTOMER_ADMIN') = 'CUSTOMER_ADMIN' then new.id
      else null
    end
  );
  return new;
end;
$$;

-- Ensure trigger is present
drop trigger if exists on_auth_user_created on auth.users;
create trigger on_auth_user_created
  after insert on auth.users
  for each row execute procedure public.handle_new_user();

-- Upgrade legacy customer self-signups into organization owners.
update public.profiles
set role = 'CUSTOMER_ADMIN',
    organization_admin_id = id
where role = 'CUSTOMER'
  and organization_admin_id = id;

-- Harden legacy helper functions that may still exist in older projects
alter function if exists public.update_profile_admin(uuid, jsonb) set search_path = public;

-- 11. POLICIES
alter table profiles enable row level security;
drop policy if exists "Public profiles" on profiles;
drop policy if exists "Public profiles are viewable by everyone." on profiles;
drop policy if exists "Profiles scoped read" on profiles;
create policy "Profiles scoped read" on profiles
for select
using (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_profile(id, organization_admin_id, branch_id, (select auth.uid()))
);
drop policy if exists "Users can update own profile" on profiles;
drop policy if exists "Users can update own profile." on profiles;
drop policy if exists "Profiles update access" on profiles;
create policy "Profiles update access" on profiles
for update
using (
  (select auth.uid()) = id
  or public.is_admin_user((select auth.uid()))
)
with check (
  (select auth.uid()) = id
  or public.is_admin_user((select auth.uid()))
);
drop policy if exists "Enable insert for authenticated users only" on profiles;
drop policy if exists "Admins can update any profile" on profiles;
drop policy if exists "Admins can delete profiles" on profiles;
drop policy if exists "Admins manage profiles" on profiles;
create policy "Admins delete profiles" on profiles
for delete
using (public.is_admin_user((select auth.uid())));

alter table customer_branches enable row level security;
drop policy if exists "Customer branches scoped read" on customer_branches;
drop policy if exists "Customer branches insert access" on customer_branches;
drop policy if exists "Customer branches update access" on customer_branches;
drop policy if exists "Customer branches delete access" on customer_branches;
create policy "Customer branches scoped read" on customer_branches
for select
using (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_record(organization_admin_id, id, (select auth.uid()))
);
create policy "Customer branches insert access" on customer_branches
for insert
with check (
  public.is_admin_user((select auth.uid()))
  or organization_admin_id = (select auth.uid())
);
create policy "Customer branches update access" on customer_branches
for update
using (
  public.is_admin_user((select auth.uid()))
  or organization_admin_id = (select auth.uid())
)
with check (
  public.is_admin_user((select auth.uid()))
  or organization_admin_id = (select auth.uid())
);
create policy "Customer branches delete access" on customer_branches
for delete
using (
  public.is_admin_user((select auth.uid()))
  or organization_admin_id = (select auth.uid())
);

alter table categories enable row level security;
drop policy if exists "Public read categories" on categories;
drop policy if exists "Public categories read" on categories;
create policy "Public categories read" on categories for select using (true);
drop policy if exists "Admins manage categories" on categories;
drop policy if exists "Admin categories manage" on categories;
create policy "Admin categories insert" on categories
for insert
with check (public.is_admin_user((select auth.uid())));
create policy "Admin categories update" on categories
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Admin categories delete" on categories
for delete
using (public.is_admin_user((select auth.uid())));

alter table products enable row level security;
drop policy if exists "Public read products" on products;
drop policy if exists "Public products read" on products;
create policy "Public products read" on products for select using (true);
drop policy if exists "Admins manage products" on products;
drop policy if exists "Admin products manage" on products;
create policy "Admin products insert" on products
for insert
with check (public.is_admin_user((select auth.uid())));
create policy "Admin products update" on products
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Admin products delete" on products
for delete
using (public.is_admin_user((select auth.uid())));

alter table product_branch_prices enable row level security;
drop policy if exists "Product branch prices scoped read" on product_branch_prices;
drop policy if exists "Product branch prices insert" on product_branch_prices;
drop policy if exists "Product branch prices update" on product_branch_prices;
drop policy if exists "Product branch prices delete" on product_branch_prices;
create policy "Product branch prices scoped read" on product_branch_prices
for select
using (
  public.is_admin_user((select auth.uid()))
  or exists (
    select 1
    from customer_branches cb
    where cb.id = product_branch_prices.branch_id
      and public.can_access_customer_record(cb.organization_admin_id, cb.id, (select auth.uid()))
  )
);
create policy "Product branch prices insert" on product_branch_prices
for insert
with check (public.is_admin_user((select auth.uid())));
create policy "Product branch prices update" on product_branch_prices
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Product branch prices delete" on product_branch_prices
for delete
using (public.is_admin_user((select auth.uid())));

alter table orders enable row level security;
drop policy if exists "Users can see own orders" on orders;
drop policy if exists "Users can create orders" on orders;
drop policy if exists "Admins can update orders" on orders;
drop policy if exists "Admins can delete orders" on orders;
drop policy if exists "Orders scoped access" on orders;
drop policy if exists "Orders scoped read" on orders;
drop policy if exists "Orders create access" on orders;
drop policy if exists "Orders update access" on orders;
drop policy if exists "Orders delete access" on orders;
create policy "Orders scoped read" on orders
for select
using (
  public.is_admin_user((select auth.uid()))
  or customer_id = (select auth.uid())
  or public.can_access_customer_record(customer_id, branch_id, (select auth.uid()))
);
create policy "Orders create access" on orders
for insert
with check (
  public.is_admin_user((select auth.uid()))
  or customer_id = (select auth.uid())
  or public.can_access_customer_record(customer_id, branch_id, (select auth.uid()))
);
create policy "Orders update access" on orders
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Orders delete access" on orders
for delete
using (public.is_admin_user((select auth.uid())));

alter table order_items enable row level security;
drop policy if exists "Order items accessible" on order_items;
drop policy if exists "Order items scoped read" on order_items;
drop policy if exists "Order items scoped insert" on order_items;
drop policy if exists "Order items admin update" on order_items;
drop policy if exists "Order items admin delete" on order_items;
create policy "Order items scoped read" on order_items
for select
using (
  exists (
    select 1
    from orders o
    where o.id = order_items.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
  )
);
create policy "Order items scoped insert" on order_items
for insert
with check (
  exists (
    select 1
    from orders o
    where o.id = order_items.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
  )
);
create policy "Order items admin update" on order_items
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Order items admin delete" on order_items
for delete
using (public.is_admin_user((select auth.uid())));

alter table order_approvals enable row level security;
drop policy if exists "Order approvals scoped access" on order_approvals;
drop policy if exists "Order approvals scoped read" on order_approvals;
drop policy if exists "Order approvals write access" on order_approvals;
drop policy if exists "Order approvals admin update" on order_approvals;
drop policy if exists "Order approvals admin delete" on order_approvals;
create policy "Order approvals scoped read" on order_approvals
for select
using (
  exists (
    select 1 from orders o
    where o.id = order_approvals.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
  )
);
create policy "Order approvals write access" on order_approvals
for insert
with check (
  public.is_admin_user((select auth.uid()))
  or exists (
    select 1 from orders o
    where o.id = order_approvals.order_id
      and o.customer_admin_id = (select auth.uid())
  )
);
create policy "Order approvals admin update" on order_approvals
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Order approvals admin delete" on order_approvals
for delete
using (public.is_admin_user((select auth.uid())));

alter table order_events enable row level security;
drop policy if exists "Order events scoped access" on order_events;
drop policy if exists "Order events scoped read" on order_events;
drop policy if exists "Order events write access" on order_events;
drop policy if exists "Order events admin update" on order_events;
drop policy if exists "Order events admin delete" on order_events;
create policy "Order events scoped read" on order_events
for select
using (
  exists (
    select 1 from orders o
    where o.id = order_events.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
  )
);
create policy "Order events write access" on order_events
for insert
with check (
  public.is_admin_user((select auth.uid()))
  or exists (
    select 1 from orders o
    where o.id = order_events.order_id
      and (
        o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
        or o.customer_admin_id = (select auth.uid())
      )
  )
);
create policy "Order events admin update" on order_events
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Order events admin delete" on order_events
for delete
using (public.is_admin_user((select auth.uid())));

alter table shipments enable row level security;
drop policy if exists "Shipments scoped access" on shipments;
drop policy if exists "Shipments scoped read" on shipments;
drop policy if exists "Shipments admin write" on shipments;
create policy "Shipments scoped read" on shipments
for select
using (
  exists (
    select 1 from orders o
    where o.id = shipments.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
 )
);
create policy "Shipments admin insert" on shipments
for insert
with check (public.is_admin_user((select auth.uid())));
create policy "Shipments admin update" on shipments
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Shipments admin delete" on shipments
for delete
using (public.is_admin_user((select auth.uid())));

alter table invoices enable row level security;
drop policy if exists "Users can see own invoices" on invoices;
drop policy if exists "Admins can manage invoices" on invoices;
drop policy if exists "Invoices scoped access" on invoices;
create policy "Invoices scoped access" on invoices
for all
using (
  exists (
    select 1 from orders o
    where o.id = invoices.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
  )
)
with check (
  exists (
    select 1 from orders o
    where o.id = invoices.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
  )
);

alter table payment_transactions enable row level security;
drop policy if exists "Payments scoped access" on payment_transactions;
drop policy if exists "Payments scoped read" on payment_transactions;
drop policy if exists "Payments scoped write" on payment_transactions;
drop policy if exists "Payments scoped update" on payment_transactions;
drop policy if exists "Payments delete access" on payment_transactions;
create policy "Payments scoped read" on payment_transactions
for select
using (
  exists (
    select 1 from orders o
    where o.id = payment_transactions.order_id
      and (
        public.is_admin_user((select auth.uid()))
        or o.customer_id = (select auth.uid())
        or public.can_access_customer_record(o.customer_id, o.branch_id, (select auth.uid()))
      )
  )
);
create policy "Payments scoped write" on payment_transactions
for insert
with check (public.is_admin_user((select auth.uid())));
create policy "Payments scoped update" on payment_transactions
for update
using (public.is_admin_user((select auth.uid())))
with check (public.is_admin_user((select auth.uid())));
create policy "Payments delete access" on payment_transactions
for delete
using (public.is_admin_user((select auth.uid())));

alter table payment_webhook_events enable row level security;
drop policy if exists "Payment webhook events admin read" on payment_webhook_events;
drop policy if exists "Payment webhook events admin write" on payment_webhook_events;
create policy "Payment webhook events admin read" on payment_webhook_events
for select using (public.is_admin_user((select auth.uid())));
create policy "Payment webhook events admin insert" on payment_webhook_events
for insert with check (public.is_admin_user((select auth.uid())));
create policy "Payment webhook events admin update" on payment_webhook_events
for update using (public.is_admin_user((select auth.uid()))) with check (public.is_admin_user((select auth.uid())));
create policy "Payment webhook events admin delete" on payment_webhook_events
for delete using (public.is_admin_user((select auth.uid())));

alter table inventory_items enable row level security;
drop policy if exists "Inventory scoped access" on inventory_items;
create policy "Inventory scoped access" on inventory_items
for all
using (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_record(organization_admin_id, branch_id, (select auth.uid()))
)
with check (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_record(organization_admin_id, branch_id, (select auth.uid()))
);

alter table inventory_usage_logs enable row level security;
drop policy if exists "Inventory usage scoped access" on inventory_usage_logs;
create policy "Inventory usage scoped access" on inventory_usage_logs
for all
using (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_record(organization_admin_id, branch_id, (select auth.uid()))
)
with check (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_record(organization_admin_id, branch_id, (select auth.uid()))
);

alter table inventory_reorder_rules enable row level security;
drop policy if exists "Reorder rules scoped access" on inventory_reorder_rules;
create policy "Reorder rules scoped access" on inventory_reorder_rules
for all
using (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_record(organization_admin_id, branch_id, (select auth.uid()))
)
with check (
  public.is_admin_user((select auth.uid()))
  or public.can_access_customer_record(organization_admin_id, branch_id, (select auth.uid()))
);

alter table testimonials enable row level security;
drop policy if exists "Public read testimonials" on testimonials;
drop policy if exists "Users can create testimonials" on testimonials;
drop policy if exists "Testimonials scoped access" on testimonials;
create policy "Testimonials scoped access" on testimonials for select using (true);
drop policy if exists "Testimonials customer insert" on testimonials;
create policy "Testimonials customer insert" on testimonials
for insert with check (
  customer_id = (select auth.uid())
  or public.is_customer_org_member(customer_id)
  or public.is_admin_user((select auth.uid()))
);
drop policy if exists "Testimonials admin manage" on testimonials;
create policy "Testimonials admin update" on testimonials
for update using (public.is_admin_user((select auth.uid()))) with check (public.is_admin_user((select auth.uid())));
create policy "Testimonials admin delete" on testimonials
for delete using (public.is_admin_user((select auth.uid())));

alter table inquiries enable row level security;
drop policy if exists "Public create inquiries" on inquiries;
drop policy if exists "Admins manage inquiries" on inquiries;
drop policy if exists "Inquiries public insert" on inquiries;
create policy "Inquiries public insert" on inquiries
for insert
with check (
  coalesce(length(trim(name)), 0) > 0
  and coalesce(length(trim(email)), 0) > 0
  and coalesce(length(trim(message)), 0) > 0
  and status = 'New'
);
drop policy if exists "Inquiries admin read" on inquiries;
create policy "Inquiries admin read" on inquiries
for select using (public.is_admin_user((select auth.uid())));
create policy "Inquiries admin update" on inquiries
for update using (public.is_admin_user((select auth.uid()))) with check (public.is_admin_user((select auth.uid())));
create policy "Inquiries admin delete" on inquiries
for delete using (public.is_admin_user((select auth.uid())));

alter table notifications enable row level security;
drop policy if exists "Users read own notifications" on notifications;
drop policy if exists "Users mark read" on notifications;
drop policy if exists "System creates notifications" on notifications;
drop policy if exists "Notifications owner read" on notifications;
create policy "Notifications owner read" on notifications
for select using (user_id = (select auth.uid()) or public.is_admin_user((select auth.uid())));
drop policy if exists "Notifications owner update" on notifications;
create policy "Notifications owner update" on notifications
for update using (user_id = (select auth.uid()) or public.is_admin_user((select auth.uid())));
drop policy if exists "Notifications admin insert" on notifications;
create policy "Notifications admin insert" on notifications
for insert with check (public.is_admin_user((select auth.uid())));

alter table site_content enable row level security;
drop policy if exists "Public read content" on site_content;
drop policy if exists "Admins update content" on site_content;
drop policy if exists "Site content public read" on site_content;
create policy "Site content public read" on site_content for select using (true);
drop policy if exists "Site content admin write" on site_content;
create policy "Site content admin insert" on site_content
for insert with check (public.is_admin_user((select auth.uid())));
create policy "Site content admin update" on site_content
for update using (public.is_admin_user((select auth.uid()))) with check (public.is_admin_user((select auth.uid())));
create policy "Site content admin delete" on site_content
for delete using (public.is_admin_user((select auth.uid())));

-- 12. CMS SEED DATA
INSERT INTO site_content (key, value, section) 
VALUES 
(
  'home_hero', 
  '{"title": "Seamless Procurement for Modern Enterprise", "subtitle": "AGM Group provides high-performance Op-Tech infrastructure for enterprise supplies and asset management.", "ctaPrimary": "Explore Catalog", "ctaSecondary": "Talk to Sales"}', 
  'home'
),
(
  'footer_info',
  '{"companyName": "AGM GROUP", "description": "Operational excellence through technology-driven procurement.", "copyrightText": "© 2025 AGM Group. All rights reserved."}',
  'global'
),
(
  'contact_info',
  '{"title": "Start Your Transformation", "subtitle": "Connect with our enterprise team.", "email": "sales@agm.com", "phone": "+91 000 000 0000", "address": "123 Business Lane", "inquiryOptions": ["Demo", "Quote"]}',
  'global'
),
(
  'social_links',
  '{"linkedin": "https://linkedin.com", "twitter": "https://twitter.com"}',
  'global'
),
(
  'logo_config',
  '{"type": "image", "text": "AGM GROUP", "height": 150, "footerHeight": 60, "imageUrl": "https://i.ibb.co/vzPRY40P/agm-logo.png"}',
  'global'
),
(
  'theme_config',
  '{"primaryColor": "#8b5cf6", "borderRadius": "xl", "fontFamily": "Inter"}',
  'global'
)
ON CONFLICT (key) DO NOTHING;
`;

export const SQL_RESET_SCRIPT = `
DROP TRIGGER IF EXISTS on_auth_user_created ON auth.users;
DROP FUNCTION IF EXISTS public.handle_new_user();
DROP FUNCTION IF EXISTS public.promote_to_admin(text);
DROP FUNCTION IF EXISTS public.is_admin_user(uuid);
DROP FUNCTION IF EXISTS public.can_access_customer_profile(uuid, uuid, bigint, uuid);
DROP FUNCTION IF EXISTS public.can_access_customer_record(uuid, bigint, uuid);
DROP FUNCTION IF EXISTS public.is_customer_org_member(uuid, uuid);
DROP FUNCTION IF EXISTS public.is_db_setup();
DROP FUNCTION IF EXISTS public.missing_required_tables();
DROP TABLE IF EXISTS site_content, inquiries, notifications, testimonials, payment_webhook_events, payment_transactions, inventory_usage_logs, inventory_reorder_rules, inventory_items, shipments, order_events, order_approvals, invoices, order_items, orders, product_branch_prices, products, categories, customer_branches, profiles CASCADE;
`;

// Configuration Helper
const getSupabaseConfig = () => {
  const env = ((import.meta as ImportMeta).env ?? {}) as Record<string, string | undefined>;
  const url = env['VITE_SUPABASE_URL']?.trim() || '';
  const key = env['VITE_SUPABASE_ANON_KEY']?.trim() || '';

  if (!url || !key) return null;
  return { url, key };
};

export const hasValidConfig = () => {
    const config = getSupabaseConfig();
    return !!(config && config.url && config.key);
};

export const listMissingRequiredTables = async (): Promise<string[]> => {
    const config = getSupabaseConfig();
    if (!config?.url || !config?.key) return [...REQUIRED_TABLES];

    const baseUrl = `${config.url.replace(/\/$/, '')}/rest/v1`;
    const headers = {
      apikey: config.key,
      Authorization: `Bearer ${config.key}`
    };

    const checks = await Promise.all(
      REQUIRED_TABLES.map(async (table) => {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 5000);
        try {
          const response = await fetch(`${baseUrl}/${table}?select=*&limit=1`, {
            headers,
            signal: controller.signal
          });
          if (response.ok) return null;

          let payload: any = null;
          try {
            payload = await response.json();
          } catch {
            payload = null;
          }

          const message = String(payload?.message || '').toLowerCase();
          if (
            response.status === 404 ||
            payload?.code === 'PGRST205' ||
            message.includes(`could not find the table 'public.${table}'`)
          ) {
            return table;
          }
          return null;
        } catch {
          return null;
        } finally {
          window.clearTimeout(timeoutId);
        }
      })
    );

    return checks.filter((table): table is (typeof REQUIRED_TABLES)[number] => Boolean(table));
};

let _supabaseClient: any = null;
export const getClient = () => {
    if (_supabaseClient) return _supabaseClient;
    const config = getSupabaseConfig();
    if (!config) {
        throw new Error("Setup Required: Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY.");
    }
    _supabaseClient = createClient(config.url, config.key);
    return _supabaseClient;
};

export const checkConnection = async () => {
    try {
        const config = getSupabaseConfig();
        if (!config) {
          return {
            success: false,
            message: "Connection credentials missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY."
          };
        }
        
        const client = createClient(config.url, config.key);
        const { data: missing, error: missingErr } = await client.rpc('missing_required_tables');
        if (!missingErr && Array.isArray(missing)) {
            if (missing.length > 0) {
              return {
                success: true,
                message: `Connected, setup needed. Missing tables: ${missing.join(', ')}`
              };
            }
            return { success: true, message: "Connected & Ready." };
        }

        const { data, error } = await client.rpc('is_db_setup');
        if (error) {
            if (
              error.message.includes('does not exist') ||
              error.message.includes('is_db_setup') ||
              error.message.includes('missing_required_tables')
            ) {
                const fallbackMissing = await listMissingRequiredTables();
                if (fallbackMissing.length > 0) {
                  return {
                    success: true,
                    message: `Connected, setup needed. Missing tables: ${fallbackMissing.join(', ')}`
                  };
                }
                return { success: true, message: "Connected to Supabase! Tables need to be created (Step 1)." };
            }
            return { success: false, message: error.message };
        }
        if (data) {
          const fallbackMissing = await listMissingRequiredTables();
          if (fallbackMissing.length > 0) {
            return {
              success: true,
              message: `Connected, setup needed. Missing tables: ${fallbackMissing.join(', ')}`
            };
          }
        }
        return { success: true, message: data ? "Connected & Ready." : "Connected, Setup Needed." };
    } catch (e: any) {
        return { success: false, message: e.message };
    }
};

export const createAdminAccount = async (email: string, password: string, name: string) => {
    const client = getClient();
    const { error } = await client.auth.signUp({
        email, password, options: { data: { name, role: 'ADMIN' } }
    });
    if (error) throw error;
    return "Admin account created.";
};

export const fixAdminRole = async (email: string) => {
    const client = getClient();
    const { data, error } = await client.rpc('promote_to_admin', { target_email: email });
    if (error) throw error;
    return data;
};

const mapProfile = (profile: any): User => ({
  ...profile,
  billingAddress: profile.billing_address,
  shippingAddress: profile.shipping_address,
  gstNumber: profile.gst_number,
  isApproved: profile.is_approved,
  organizationAdminId: profile.organization_admin_id,
  branchId: profile.branch_id ?? null
});

const mapBranch = (branch: any): CustomerBranch => ({
  id: branch.id,
  organizationAdminId: branch.organization_admin_id,
  name: branch.name,
  billingAddress: branch.billing_address,
  shippingAddress: branch.shipping_address,
  gstNumber: branch.gst_number || undefined,
  isDefault: Boolean(branch.is_default),
  createdAt: new Date(branch.created_at),
  updatedAt: new Date(branch.updated_at)
});

const mapInventoryUsageLog = (row: any): InventoryUsageLog => ({
  id: row.id,
  inventoryItemId: row.inventory_item_id,
  organizationAdminId: row.organization_admin_id,
  branchId: row.branch_id,
  productId: row.product_id,
  productName: row.product_name,
  variationId: row.variation_id || undefined,
  variationName: row.variation_name || undefined,
  quantity: Number(row.quantity || 0),
  note: row.note || undefined,
  createdBy: row.created_by,
  createdByName: row.created_by_name || undefined,
  createdAt: new Date(row.created_at)
});

const mapBranchPrice = (row: any): ProductBranchPrice => ({
  id: row.id,
  organizationAdminId: row.organization_admin_id,
  branchId: row.branch_id,
  productId: row.product_id,
  variationId: row.variation_id || undefined,
  price: Number(row.price || 0),
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at)
});

const mapInvoice = (invData: any): Invoice => ({
  ...invData,
  orderId: invData.order_id,
  customerId: invData.customer_id,
  taxRate: Number(invData.tax_rate || 0),
  issuedAt: new Date(invData.issued_at),
  dueDate: new Date(invData.due_date)
});

const mapShipment = (shipment: any): Shipment => ({
  id: shipment.id,
  orderId: shipment.order_id,
  trackingNumber: shipment.tracking_number,
  courierPartner: shipment.courier_partner,
  status: shipment.status,
  shippedBy: shipment.shipped_by,
  shippedAt: new Date(shipment.shipped_at),
  deliveredBy: shipment.delivered_by,
  deliveredAt: shipment.delivered_at ? new Date(shipment.delivered_at) : undefined
});

const mapOrderEvent = (event: any): OrderEvent => ({
  id: event.id,
  orderId: event.order_id,
  eventType: event.event_type,
  actorId: event.actor_id,
  actorName: event.actor_name,
  fromStatus: event.from_status,
  toStatus: event.to_status,
  details: event.details,
  createdAt: new Date(event.created_at)
});

const mapOrder = (order: DatabaseOrderRow, items: DatabaseOrderItemRow[], shipment?: DatabaseShipmentRow): Order => ({
  ...order,
  status: order.status as OrderStatus,
  customerId: order.customer_id,
  customerName: order.customer_name,
  branchId: order.branch_id,
  branchName: order.branch_name || undefined,
  billingAddress: order.billing_address || undefined,
  shippingAddress: order.shipping_address || undefined,
  gstNumber: order.gst_number || undefined,
  totalAmount: Number(order.total_amount),
  createdAt: new Date(order.created_at),
  customerAdminId: order.customer_admin_id,
  submittedById: order.submitted_by,
  approvedById: order.approved_by,
  approvedAt: order.approved_at ? new Date(order.approved_at) : undefined,
  deniedById: order.denied_by,
  deniedAt: order.denied_at ? new Date(order.denied_at) : undefined,
  approvalRemark: order.approval_remark || undefined,
  shipmentId: shipment?.id || null,
  trackingNumber: shipment?.tracking_number || undefined,
  courierPartner: shipment?.courier_partner || undefined,
  shippedAt: shipment?.shipped_at ? new Date(shipment.shipped_at) : undefined,
  deliveredAt: shipment?.delivered_at ? new Date(shipment.delivered_at) : undefined,
  items: (items || []).map((i) => ({
    id: i.id,
    productId: i.product_id,
    productName: i.product_name,
    quantity: i.quantity,
    pricePerUnit: Number(i.price_per_unit),
    variationId: i.variation_id,
    variationName: i.variation_name,
    packed: i.packed,
    receivedQuantity: i.received_quantity,
    receiveRemark: i.receive_remark
  }))
});

const toIsoNow = () => new Date().toISOString();

export const supabaseBackend: BackendService = {
  auth: {
    async login(email, password) {
      const client = getClient();
      const { data, error } = await client.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const { data: profile } = await client.from('profiles').select('*').eq('id', data.user.id).single();
      return mapProfile(profile);
    },
    async signup(data) {
      const client = getClient();
      // Pass all metadata in the initial signUp call
      const { data: authData, error } = await client.auth.signUp({
        email: data.email, 
        password: data.password || 'password123',
        options: { 
          data: { 
            name: data.name, 
            role: UserRole.CUSTOMER_ADMIN,
            phone: data.phone,
            gst_number: data.gstNumber,
            billing_address: data.billingAddress,
            shipping_address: data.shippingAddress,
            branch_id: null
          } 
        }
      });
      if (error) throw error;
      
      // Fallback manual update in case trigger delay or missed mapping
      await client.from('profiles').update({
          role: UserRole.CUSTOMER_ADMIN,
          phone: data.phone,
          gst_number: data.gstNumber,
          billing_address: data.billingAddress,
          shipping_address: data.shippingAddress,
          organization_admin_id: authData.user!.id,
          branch_id: null
      }).eq('id', authData.user!.id);

      return {
        id: authData.user!.id,
        name: data.name,
        email: data.email,
        role: UserRole.CUSTOMER_ADMIN,
        organizationAdminId: authData.user!.id
      };
    },
    async forgotPassword(email) { await getClient().auth.resetPasswordForEmail(email); },
    async logout() { await getClient().auth.signOut(); }
  },
  categories: {
    async list() {
      const { data } = await getClient().from('categories').select('*').order('name');
      return (data || []).map((c: any) => ({ ...c, parentId: c.parent_id }));
    },
    async add(name, parentId) {
      const { data } = await getClient().from('categories').insert([{ name, parent_id: parentId }]).select().single();
      return { ...data, parentId: data.parent_id };
    },
    async update(id, name, parentId) {
      const { data } = await getClient().from('categories').update({ name, parent_id: parentId }).eq('id', id).select().single();
      return { ...data, parentId: data.parent_id };
    },
    async delete(id) { await getClient().from('categories').delete().eq('id', id); }
  },
  products: {
    async list() {
      const client = getClient();
      const [{ data }, { data: branchPriceRows }] = await Promise.all([
        client.from('products').select('*').order('name'),
        client.from('product_branch_prices').select('*')
      ]);

      const branchBasePrices: Record<string, Record<string, number>> = {};
      const branchVariationPrices: Record<string, Record<string, Record<string, number>>> = {};
      (branchPriceRows || []).forEach((row: any) => {
        const branchId = String(row.branch_id);
        const productId = String(row.product_id);
        const price = Number(row.price || 0);
        if (row.variation_id) {
          if (!branchVariationPrices[productId]) branchVariationPrices[productId] = {};
          if (!branchVariationPrices[productId][branchId]) branchVariationPrices[productId][branchId] = {};
          branchVariationPrices[productId][branchId][row.variation_id] = price;
        } else {
          if (!branchBasePrices[productId]) branchBasePrices[productId] = {};
          branchBasePrices[productId][branchId] = price;
        }
      });

      return (data || []).map((p: any) => ({
          ...p, 
          hsnCode: p.hsn_code,
          stockQuantity: p.stock_quantity, 
          basePrice: Number(p.base_price),
          categoryId: p.category_id, 
          subCategoryId: p.subcategory_id,
          customerPrices: p.customer_prices, 
          customerVariationPrices: p.customer_variation_prices,
          branchPrices: branchBasePrices[String(p.id)] || {},
          branchVariationPrices: branchVariationPrices[String(p.id)] || {},
          imageUrl: p.image_url,
          variations: p.variations
      }));
    },
    async add(data) {
      const { data: p, error } = await getClient().from('products').insert([{
          name: data.name, 
          description: data.description, 
          image_url: data.imageUrl,
          hsn_code: data.hsnCode,
          stock_quantity: data.stockQuantity, 
          base_price: data.basePrice,
          category_id: data.categoryId, 
          variations: data.variations
      }]).select().single();
      if (error) throw error;
      return { 
          ...p, 
          hsnCode: p.hsn_code,
          stockQuantity: p.stock_quantity, 
          basePrice: Number(p.base_price), 
          imageUrl: p.image_url,
          categoryId: p.category_id,
          variations: p.variations,
          branchPrices: {},
          branchVariationPrices: {}
      };
    },
    async update(id, updates) {
      const payload: any = {};
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.hsnCode !== undefined) payload.hsn_code = updates.hsnCode;
      if (updates.stockQuantity !== undefined) payload.stock_quantity = updates.stockQuantity;
      if (updates.basePrice !== undefined) payload.base_price = updates.basePrice;
      if (updates.imageUrl !== undefined) payload.image_url = updates.imageUrl;
      if (updates.categoryId !== undefined) payload.category_id = updates.categoryId;
      if (updates.variations !== undefined) payload.variations = updates.variations;
      if (updates.customerPrices !== undefined) payload.customer_prices = updates.customerPrices;
      if (updates.customerVariationPrices !== undefined) payload.customer_variation_prices = updates.customerVariationPrices;

      const { data, error } = await getClient().from('products').update(payload).eq('id', id).select().single();
      if (error) throw error;
      return { 
          ...data, 
          hsnCode: data.hsn_code,
          stockQuantity: data.stock_quantity, 
          basePrice: Number(data.base_price), 
          imageUrl: data.image_url,
          categoryId: data.category_id,
          variations: data.variations,
          customerPrices: data.customer_prices,
          customerVariationPrices: data.customer_variation_prices,
          branchPrices: {},
          branchVariationPrices: {}
      };
    },
    async delete(id) { 
      const { error } = await getClient().from('products').delete().eq('id', id);
      if (error) throw error;
    }
  },
  orders: {
    async list() {
      const client = getClient();
      const { data: rows } = await client
        .from('orders')
        .select('*, order_items(*)')
        .order('created_at', { ascending: false });
      const typedRows = (rows || []) as DatabaseOrderRow[];
      const orderIds = typedRows.map((o) => o.id);
      const shipmentsByOrder: Record<number, DatabaseShipmentRow> = {};
      if (orderIds.length > 0) {
        try {
          const { data: shipmentRows } = await client.from('shipments').select('*').in('order_id', orderIds);
          ((shipmentRows || []) as DatabaseShipmentRow[]).forEach((shipment) => {
            shipmentsByOrder[shipment.order_id] = shipment;
          });
        } catch {
          // Backward compatibility for databases that haven't applied shipment migration yet.
        }
      }
      return typedRows.map((o) => mapOrder(o, o.order_items || [], shipmentsByOrder[o.id]));
    },
    async create(data: OrderCreatePayload) {
      const client = getClient();
      const submittedById = data.submittedById || data.customerId;
      const customerAdminId = data.customerAdminId || submittedById;
      const computedTotal = data.items.reduce(
        (sum, item) => sum + Number(item.pricePerUnit || 0) * Number(item.quantity || 0),
        0
      );

      const { data: order, error: orderInsertError } = await client
        .from('orders')
        .insert([
          {
            customer_id: data.customerId,
            customer_name: data.customerName,
            branch_id: data.branchId || null,
            branch_name: data.branchName || null,
            billing_address: data.billingAddress || null,
            shipping_address: data.shippingAddress || null,
            gst_number: data.gstNumber || null,
            status: data.status,
            total_amount: computedTotal,
            submitted_by: submittedById,
            customer_admin_id: customerAdminId
          }
        ])
        .select()
        .single();
      if (orderInsertError) {
        throw new Error(`Failed to create order: ${orderInsertError.message}`);
      }
      if (!order) {
        throw new Error('Failed to create order: order insert returned no data.');
      }

      const items = data.items.map((i) => ({
        order_id: order.id,
        product_id: i.productId,
        product_name: i.productName,
        quantity: i.quantity,
        price_per_unit: i.pricePerUnit,
        variation_id: i.variationId,
        variation_name: i.variationName
      }));
      if (items.length > 0) {
        const { error: orderItemsInsertError } = await client.from('order_items').insert(items);
        if (orderItemsInsertError) {
          throw new Error(`Failed to create order items: ${orderItemsInsertError.message}`);
        }
      }

      try {
        await client.from('order_events').insert([
          {
            order_id: order.id,
            event_type: 'ORDER_SUBMITTED',
            actor_id: submittedById,
            actor_name: data.customerName,
            from_status: null,
            to_status: data.status,
            details: `Order submitted with ${items.length} line item(s).`
          }
        ]);
      } catch {
        // Non-fatal in pre-migration environments.
      }

      const { data: finalItems, error: finalItemsError } = await client.from('order_items').select('*').eq('order_id', order.id);
      if (finalItemsError) {
        throw new Error(`Failed to load created order items: ${finalItemsError.message}`);
      }
      return mapOrder(order as DatabaseOrderRow, (finalItems || []) as DatabaseOrderItemRow[]);
    },
    async updateStatus(id, status, prices, actor) {
      const client = getClient();

      const { data: currentOrder, error: orderError } = await client.from('orders').select('*').eq('id', id).single();
      if (orderError) throw orderError;

      // 1. Process quoted prices if moving to APPROVED
      if (status === OrderStatus.APPROVED && prices) {
          const { data: items } = await client.from('order_items').select('*').eq('order_id', id);
          if (items) {
              for (const item of items) {
                  const key = `${item.product_id}-${item.variation_id || 'base'}`;
                  const agreedPrice = prices[key];
                  if (agreedPrice !== undefined) {
                      await client.from('order_items').update({ price_per_unit: agreedPrice }).eq('id', item.id);
                  }
              }
          }
      }

      const { data: pricedItems } = await client.from('order_items').select('*').eq('order_id', id);
      const totalAmount = (pricedItems || []).reduce(
        (sum: number, item: any) => sum + Number(item.price_per_unit || 0) * Number(item.quantity || 0),
        0
      );

      // 2. Update Order Status & Total
      const updates: any = { status, total_amount: totalAmount };
      if (status === OrderStatus.PENDING_CUSTOMER_APPROVAL) {
        updates.approved_by = null;
        updates.approved_at = null;
        updates.denied_by = null;
        updates.denied_at = null;
        updates.approval_remark = null;
      }
      const { data: order } = await client.from('orders').update(updates).eq('id', id).select().single();

      try {
        await client.from('order_events').insert([
          {
            order_id: id,
            event_type: 'STATUS_CHANGED',
            actor_id: actor?.id || null,
            actor_name: actor?.name || null,
            from_status: currentOrder.status,
            to_status: status,
            details: `Order status changed from ${currentOrder.status} to ${status}.`
          }
        ]);
      } catch {
        // Non-fatal in pre-migration environments.
      }

      // 3. Keep invoice state in sync with workflow transitions
      let invoice: Invoice | undefined;
      if (status === OrderStatus.COMPLETED) {
          const { data: existingInvoices } = await client
            .from('invoices')
            .select('*')
            .eq('order_id', id)
            .order('issued_at', { ascending: false })
            .limit(1);

          if (existingInvoices && existingInvoices.length > 0) {
              invoice = mapInvoice(existingInvoices[0]);
          } else {
              const dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + 30);
              const { data: invData } = await client.from('invoices').insert([{
                  order_id: id,
                  customer_id: order.customer_id,
                  customer_name: order.customer_name,
                  amount: totalAmount,
                  status: 'Unpaid',
                  due_date: dueDate.toISOString()
              }]).select().single();
              if (invData) {
                invoice = mapInvoice(invData);
                try {
                  await client.from('order_events').insert([
                    {
                      order_id: id,
                      event_type: 'INVOICE_CREATED',
                      actor_id: actor?.id || null,
                      actor_name: actor?.name || null,
                      from_status: status,
                      to_status: status,
                      details: `Invoice #${invData.id} generated for amount ₹${totalAmount.toFixed(2)}`
                    }
                  ]);
                } catch {
                  // Non-fatal in pre-migration environments.
                }
              }
          }
      } else if (status === OrderStatus.PAID) {
          await client.from('invoices').update({ status: 'Paid' }).eq('order_id', id);
          const { data: paidInvoices } = await client
            .from('invoices')
            .select('*')
            .eq('order_id', id)
            .order('issued_at', { ascending: false })
            .limit(1);
          if (paidInvoices && paidInvoices.length > 0) {
              invoice = mapInvoice(paidInvoices[0]);
          }
      }

      const { data: finalItems } = await client.from('order_items').select('*').eq('order_id', id);
      let shipment: any = null;
      try {
        const { data } = await client.from('shipments').select('*').eq('order_id', id).maybeSingle();
        shipment = data;
      } catch {
        shipment = null;
      }
      return { 
          order: mapOrder(order, finalItems || [], shipment || undefined),
          invoice
      };
    },
    async reviewCustomerApproval(orderId, approved, remark, actor) {
      const client = getClient();
      const nextStatus = approved ? OrderStatus.PENDING : OrderStatus.CUSTOMER_DENIED;
      const cleanRemark = (remark || '').trim();
      if (!approved && !cleanRemark) {
        throw new Error('Remark is required when denying an order.');
      }

      const { data: currentOrder, error: fetchError } = await client.from('orders').select('*').eq('id', orderId).single();
      if (fetchError) throw fetchError;

      if (currentOrder.status !== OrderStatus.PENDING_CUSTOMER_APPROVAL) {
        throw new Error(`Order #${orderId} is not awaiting customer-admin approval.`);
      }

      if (actor?.id) {
        const { data: actorProfile, error: actorError } = await client
          .from('profiles')
          .select('id, role')
          .eq('id', actor.id)
          .single();
        if (actorError) throw actorError;
        const canReview =
          !!actorProfile &&
          (['ADMIN', 'SUPER_ADMIN', 'MANAGER', 'ACCOUNTANT'].includes(actorProfile.role) ||
            currentOrder.customer_admin_id === actor.id);
        if (!canReview) {
          throw new Error('Only the assigned customer admin can approve or deny this order.');
        }
      }

      const updates: any = {
        status: nextStatus,
        approval_remark: cleanRemark || null
      };

      if (approved) {
        updates.approved_by = actor?.id || null;
        updates.approved_at = toIsoNow();
        updates.denied_by = null;
        updates.denied_at = null;
      } else {
        updates.denied_by = actor?.id || null;
        updates.denied_at = toIsoNow();
      }

      const { data: updatedOrder, error: updateError } = await client
        .from('orders')
        .update(updates)
        .eq('id', orderId)
        .select()
        .single();
      if (updateError) throw updateError;

      try {
        await client.from('order_approvals').insert([
          {
            order_id: orderId,
            actor_id: actor?.id || null,
            actor_name: actor?.name || null,
            decision: approved ? 'APPROVED' : 'DENIED',
            remark: cleanRemark || null
          }
        ]);
      } catch {
        // Non-fatal in pre-migration environments.
      }

      try {
        await client.from('order_events').insert([
          {
            order_id: orderId,
            event_type: approved ? 'CUSTOMER_APPROVED' : 'CUSTOMER_DENIED',
            actor_id: actor?.id || null,
            actor_name: actor?.name || null,
            from_status: currentOrder.status,
            to_status: nextStatus,
            details: cleanRemark || null
          }
        ]);
      } catch {
        // Non-fatal in pre-migration environments.
      }

      const { data: finalItems } = await client.from('order_items').select('*').eq('order_id', orderId);
      let shipment: any = null;
      try {
        const { data } = await client.from('shipments').select('*').eq('order_id', orderId).maybeSingle();
        shipment = data;
      } catch {
        shipment = null;
      }
      return mapOrder(updatedOrder, finalItems || [], shipment || undefined);
    },
    async listEvents(orderId) {
      let query = getClient().from('order_events').select('*').order('created_at', { ascending: false });
      if (orderId !== undefined) {
        query = query.eq('order_id', orderId);
      }
      const { data } = await query;
      return (data || []).map(mapOrderEvent);
    },
    async listShipments() {
      const { data } = await getClient().from('shipments').select('*').order('shipped_at', { ascending: false });
      return (data || []).map(mapShipment);
    },
    async ship(orderId, trackingNumber, courierPartner, actor) {
      const client = getClient();
      const payload = {
        order_id: orderId,
        tracking_number: trackingNumber,
        courier_partner: courierPartner || null,
        status: 'SHIPPED',
        shipped_by: actor?.id || null,
        shipped_at: toIsoNow(),
        delivered_by: null,
        delivered_at: null
      };
      const { data: shipment, error } = await client
        .from('shipments')
        .upsert([payload], { onConflict: 'order_id' })
        .select()
        .single();
      if (error) throw error;

      await supabaseBackend.orders.updateStatus(orderId, OrderStatus.SHIPPED, undefined, actor);
      await client.from('order_events').insert([
        {
          order_id: orderId,
          event_type: 'SHIPMENT_UPDATED',
          actor_id: actor?.id || null,
          actor_name: actor?.name || null,
          from_status: OrderStatus.SHIPPED,
          to_status: OrderStatus.SHIPPED,
          details: `Shipment updated. Tracking #${trackingNumber}${courierPartner ? ` via ${courierPartner}` : ''}`
        }
      ]);
      return mapShipment(shipment);
    },
    async markDelivered(orderId, actor) {
      const client = getClient();
      const { data: shipment, error } = await client
        .from('shipments')
        .update({
          status: 'DELIVERED',
          delivered_by: actor?.id || null,
          delivered_at: toIsoNow()
        })
        .eq('order_id', orderId)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!shipment) return null;

      await client.from('order_events').insert([
        {
          order_id: orderId,
          event_type: 'SHIPMENT_DELIVERED',
          actor_id: actor?.id || null,
          actor_name: actor?.name || null,
          from_status: OrderStatus.SHIPPED,
          to_status: OrderStatus.SHIPPED,
          details: `Shipment delivered (tracking #${shipment.tracking_number}).`
        }
      ]);
      return mapShipment(shipment);
    },
    async setItemPacked(orderId, itemId, packed, actor) {
      const client = getClient();
      const { error } = await client
        .from('order_items')
        .update({ packed })
        .eq('order_id', orderId)
        .eq('id', itemId);
      if (error) throw error;
      try {
        await client.from('order_events').insert([
          {
            order_id: orderId,
            event_type: 'PACKING_CHECK',
            actor_id: actor?.id || null,
            actor_name: actor?.name || null,
            details: `Item #${itemId} marked as ${packed ? 'packed' : 'not packed'}.`
          }
        ]);
      } catch {
        // Non-fatal in pre-migration environments.
      }
    },
    async submitReceiptChecks(orderId, checks, actor) {
      const client = getClient();
      for (const check of checks) {
        if (!check.itemId) continue;
        const { error } = await client
          .from('order_items')
          .update({
            received_quantity: check.receivedQuantity,
            receive_remark: check.remark || null
          })
          .eq('order_id', orderId)
          .eq('id', check.itemId);
        if (error) throw error;
      }
      try {
        await client.from('order_events').insert([
          {
            order_id: orderId,
            event_type: 'RECEIPT_CHECKLIST_SUBMITTED',
            actor_id: actor?.id || null,
            actor_name: actor?.name || null,
            details: `Receiving checklist submitted for ${checks.length} item(s).`
          }
        ]);
      } catch {
        // Non-fatal in pre-migration environments.
      }
    }
  },
  invoices: {
    async list() {
      const { data } = await getClient().from('invoices').select('*').order('issued_at', { ascending: false });
      return (data || []).map(mapInvoice);
    },
    async pay(id) {
      const { data } = await getClient().from('invoices').update({ status: 'Paid' }).eq('id', id).select().single();
      return mapInvoice(data);
    },
    async update(id, updates) {
      const payload: any = {};
      if (updates.status) payload.status = updates.status;
      if (updates.dueDate) payload.due_date = updates.dueDate.toISOString();
      if (updates.taxRate !== undefined) payload.tax_rate = updates.taxRate;
      if (updates.notes !== undefined) payload.notes = updates.notes;

      const { data } = await getClient().from('invoices').update(payload).eq('id', id).select().single();
      return mapInvoice(data);
    }
  },
  customers: {
    async list() {
      const { data } = await getClient().from('profiles').select('*').order('name');
      return (data || []).map(mapProfile);
    },
    async add(data) { return supabaseBackend.auth.signup(data); },
    async addAdmin(data) {
      const client = getClient();
      const { data: authData, error } = await client.auth.signUp({
        email: data.email,
        password: data.password || 'password123',
        options: {
          data: {
            name: data.name,
            role: UserRole.ADMIN,
            phone: data.phone,
            gst_number: data.gstNumber,
            billing_address: data.billingAddress,
            shipping_address: data.shippingAddress
          }
        }
      });
      if (error) throw error;

      await client
        .from('profiles')
        .update({
          role: UserRole.ADMIN,
          is_approved: true,
          organization_admin_id: null,
          phone: data.phone,
          gst_number: data.gstNumber,
          billing_address: data.billingAddress,
          shipping_address: data.shippingAddress
        })
        .eq('id', authData.user!.id);

      return {
        id: authData.user!.id,
        name: data.name,
        email: data.email,
        role: UserRole.ADMIN,
        phone: data.phone,
        gstNumber: data.gstNumber,
        billingAddress: data.billingAddress,
        shippingAddress: data.shippingAddress,
        isApproved: true,
        organizationAdminId: null
      };
    },
    async update(id, updates) {
      const payload: any = {
        name: updates.name,
        phone: updates.phone,
        billing_address: updates.billingAddress,
        shipping_address: updates.shippingAddress,
        gst_number: updates.gstNumber
      };
      if ((updates as any).organizationAdminId !== undefined) {
        payload.organization_admin_id = (updates as any).organizationAdminId || null;
      }
      if ((updates as any).branchId !== undefined) {
        payload.branch_id = (updates as any).branchId || null;
      }
      const { data } = await getClient().from('profiles').update(payload).eq('id', id).select().single();
      return mapProfile(data);
    },
    async updateRole(id, role) {
        const payload: any = { role };
        if (role === UserRole.CUSTOMER_ADMIN) {
          payload.organization_admin_id = id;
          payload.branch_id = null;
        }
        await getClient().from('profiles').update(payload).eq('id', id);
    },
    async updateApproval(id, isApproved) {
        await getClient().from('profiles').update({ is_approved: isApproved }).eq('id', id);
    },
    async delete(id) { await getClient().from('profiles').delete().eq('id', id); }
  },
  testimonials: {
    async list() {
      const { data } = await getClient().from('testimonials').select('*').order('created_at', { ascending: false });
      return (data || []).map((t: any) => ({ ...t, createdAt: new Date(t.created_at) }));
    },
    async add(data) {
      const { data: t } = await getClient().from('testimonials').insert([{
          customer_id: data.customerId,
          customer_name: data.customerName,
          product_id: data.productId,
          product_name: data.productName,
          rating: data.rating,
          comment: data.comment
      }]).select().single();
      return { ...t, createdAt: new Date(t.created_at) };
    }
  },
  inquiries: {
    async list() {
      const { data } = await getClient().from('inquiries').select('*').order('created_at', { ascending: false });
      return (data || []).map((i: any) => ({ ...i, createdAt: new Date(i.created_at) }));
    },
    async add(data) { await getClient().from('inquiries').insert([data]); },
    async updateStatus(id, status) { await getClient().from('inquiries').update({ status }).eq('id', id); }
  },
  notifications: {
    async list(userId) {
      const { data } = await getClient().from('notifications').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      return (data || []).map((n: any) => ({
        id: n.id,
        userId: n.user_id,
        title: n.title || undefined,
        message: n.message,
        type: n.type,
        isRead: n.is_read,
        externalKey: n.external_key || undefined,
        entityType: n.entity_type || undefined,
        entityId: n.entity_id || undefined,
        metadata: n.metadata || null,
        createdAt: new Date(n.created_at)
      }));
    },
    async add(userId, message, type) {
      const { data } = await getClient().from('notifications').insert([{ user_id: userId, message, type }]).select().single();
      return {
        id: data.id,
        userId: data.user_id,
        title: data.title || undefined,
        message: data.message,
        type: data.type,
        isRead: data.is_read,
        externalKey: data.external_key || undefined,
        entityType: data.entity_type || undefined,
        entityId: data.entity_id || undefined,
        metadata: data.metadata || null,
        createdAt: new Date(data.created_at)
      };
    },
    async markAsRead(id) { await getClient().from('notifications').update({ is_read: true }).eq('id', id); }
  },
  cms: {
    async list() {
      const { data } = await getClient().from('site_content').select('*');
      return data || [];
    },
    async update(key, value) { await getClient().from('site_content').upsert({ key, value }); }
  },
  branches: {
    async list(organizationAdminId) {
      let query = getClient().from('customer_branches').select('*').order('is_default', { ascending: false }).order('name');
      if (organizationAdminId) {
        query = query.eq('organization_admin_id', organizationAdminId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map(mapBranch);
    },
    async add(data: NewCustomerBranchData) {
      const payload = {
        organization_admin_id: data.organizationAdminId,
        name: data.name,
        billing_address: data.billingAddress,
        shipping_address: data.shippingAddress,
        gst_number: data.gstNumber || null,
        is_default: data.isDefault
      };
      const client = getClient();
      if (data.isDefault) {
        await client
          .from('customer_branches')
          .update({ is_default: false, updated_at: toIsoNow() })
          .eq('organization_admin_id', data.organizationAdminId);
      }
      const { data: inserted, error } = await client.from('customer_branches').insert([payload]).select().single();
      if (error) throw error;
      return mapBranch(inserted);
    },
    async update(id, updates) {
      const payload: Record<string, any> = { updated_at: toIsoNow() };
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.billingAddress !== undefined) payload.billing_address = updates.billingAddress;
      if (updates.shippingAddress !== undefined) payload.shipping_address = updates.shippingAddress;
      if (updates.gstNumber !== undefined) payload.gst_number = updates.gstNumber || null;
      if (updates.isDefault !== undefined) payload.is_default = updates.isDefault;

      const client = getClient();
      if (updates.isDefault) {
        const { data: current } = await client.from('customer_branches').select('organization_admin_id').eq('id', id).single();
        if (current?.organization_admin_id) {
          await client
            .from('customer_branches')
            .update({ is_default: false, updated_at: toIsoNow() })
            .eq('organization_admin_id', current.organization_admin_id);
        }
      }
      const { data, error } = await client.from('customer_branches').update(payload).eq('id', id).select().single();
      if (error) throw error;
      return mapBranch(data);
    },
    async delete(id) {
      const { error } = await getClient().from('customer_branches').delete().eq('id', id);
      if (error) throw error;
    }
  },
  branchPricing: {
    async upsert(input) {
      const client = getClient();
      let query = client
        .from('product_branch_prices')
        .select('*')
        .eq('branch_id', input.branchId)
        .eq('product_id', input.productId);
      if (input.variationId) {
        query = query.eq('variation_id', input.variationId);
      } else {
        query = query.is('variation_id', null);
      }
      const { data: existing } = await query.maybeSingle();
      const payload = {
        organization_admin_id: input.organizationAdminId,
        branch_id: input.branchId,
        product_id: input.productId,
        variation_id: input.variationId || null,
        price: input.price,
        updated_at: toIsoNow()
      };
      const response = existing
        ? await client.from('product_branch_prices').update(payload).eq('id', existing.id).select().single()
        : await client.from('product_branch_prices').insert([{ ...payload, created_at: toIsoNow() }]).select().single();
      if (response.error) throw response.error;
      return mapBranchPrice(response.data);
    },
    async remove(branchId, productId, variationId) {
      let query = getClient()
        .from('product_branch_prices')
        .delete()
        .eq('branch_id', branchId)
        .eq('product_id', productId);
      if (variationId) {
        query = query.eq('variation_id', variationId);
      } else {
        query = query.is('variation_id', null);
      }
      const { error } = await query;
      if (error) throw error;
    }
  },
  inventory: {
    async list(organizationAdminId) {
      const client = getClient();
      const { data: items } = await client
        .from('inventory_items')
        .select('*')
        .eq('organization_admin_id', organizationAdminId)
        .order('product_name');
      const { data: rules } = await client
        .from('inventory_reorder_rules')
        .select('*')
        .eq('organization_admin_id', organizationAdminId);

      const thresholdByKey: Record<string, number> = {};
      (rules || []).forEach((rule: any) => {
        thresholdByKey[`${rule.branch_id || 'default'}-${rule.product_id}-${rule.variation_id || 'base'}`] = Number(rule.threshold || 10);
      });

      return (items || []).map((item: any): InventoryItem => ({
        id: item.id,
        branchId: item.branch_id,
        productId: item.product_id,
        productName: item.product_name,
        variationId: item.variation_id || undefined,
        variationName: item.variation_name || undefined,
        quantity: Number(item.quantity || 0),
        lowStockThreshold: thresholdByKey[`${item.branch_id || 'default'}-${item.product_id}-${item.variation_id || 'base'}`] ?? 10,
        organizationAdminId: item.organization_admin_id
      }));
    },
    async listUsageLogs(organizationAdminId) {
      const { data, error } = await getClient()
        .from('inventory_usage_logs')
        .select('*')
        .eq('organization_admin_id', organizationAdminId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []).map(mapInventoryUsageLog);
    },
    async updateThreshold(organizationAdminId, branchId, productId, variationId, threshold) {
      const client = getClient();
      let lookup = client
        .from('inventory_reorder_rules')
        .select('*')
        .eq('organization_admin_id', organizationAdminId)
        .eq('product_id', productId);
      if (branchId) {
        lookup = lookup.eq('branch_id', branchId);
      } else {
        lookup = lookup.is('branch_id', null);
      }
      if (variationId) {
        lookup = lookup.eq('variation_id', variationId);
      } else {
        lookup = lookup.is('variation_id', null);
      }
      const { data: existing } = await lookup.maybeSingle();
      const payload = {
        organization_admin_id: organizationAdminId,
        branch_id: branchId || null,
        product_id: productId,
        variation_id: variationId || null,
        threshold,
        updated_at: toIsoNow()
      };
      const response = existing
        ? await client.from('inventory_reorder_rules').update(payload).eq('id', existing.id).select().single()
        : await client.from('inventory_reorder_rules').insert([{ ...payload, created_at: toIsoNow() }]).select().single();
      if (response.error) throw response.error;
      const data = response.data;
      return {
        id: data.id,
        organizationAdminId: data.organization_admin_id,
        branchId: data.branch_id,
        productId: data.product_id,
        variationId: data.variation_id || undefined,
        threshold: Number(data.threshold || 10),
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      } as ReorderRule;
    },
    async applyReceipt(organizationAdminId, branchId, items) {
      const client = getClient();
      for (const item of items) {
        if (!item.quantity || item.quantity <= 0) continue;
        let query = client
          .from('inventory_items')
          .select('*')
          .eq('organization_admin_id', organizationAdminId)
          .eq('product_id', item.productId);
        if (branchId) {
          query = query.eq('branch_id', branchId);
        } else {
          query = query.is('branch_id', null);
        }
        if (item.variationId) {
          query = query.eq('variation_id', item.variationId);
        } else {
          query = query.is('variation_id', null);
        }
        const { data: existing } = await query.maybeSingle();
        if (existing) {
          await client
            .from('inventory_items')
            .update({
              quantity: Number(existing.quantity || 0) + Number(item.quantity || 0),
              updated_at: toIsoNow()
            })
            .eq('id', existing.id);
        } else {
          await client.from('inventory_items').insert([
            {
              organization_admin_id: organizationAdminId,
              branch_id: branchId || null,
              product_id: item.productId,
              product_name: item.productName,
              variation_id: item.variationId || null,
              variation_name: item.variationName || null,
              quantity: item.quantity
            }
          ]);
        }

        let ruleLookup = client
          .from('inventory_reorder_rules')
          .select('*')
          .eq('organization_admin_id', organizationAdminId)
          .eq('product_id', item.productId);
        if (branchId) {
          ruleLookup = ruleLookup.eq('branch_id', branchId);
        } else {
          ruleLookup = ruleLookup.is('branch_id', null);
        }
        if (item.variationId) {
          ruleLookup = ruleLookup.eq('variation_id', item.variationId);
        } else {
          ruleLookup = ruleLookup.is('variation_id', null);
        }
        const { data: existingRule } = await ruleLookup.maybeSingle();
        if (!existingRule) {
          await client.from('inventory_reorder_rules').insert([
            {
              organization_admin_id: organizationAdminId,
              branch_id: branchId || null,
              product_id: item.productId,
              variation_id: item.variationId || null,
              threshold: 10,
              created_at: toIsoNow(),
              updated_at: toIsoNow()
            }
          ]);
        }
      }
    },
    async issueStock(organizationAdminId, inventoryItemId, quantity, note, actor) {
      const client = getClient();
      const issueQty = Math.max(0, Number(quantity || 0));
      if (!Number.isFinite(issueQty) || issueQty <= 0) {
        throw new Error('Please enter a valid issue quantity.');
      }

      const { data: existing, error: lookupError } = await client
        .from('inventory_items')
        .select('*')
        .eq('id', inventoryItemId)
        .eq('organization_admin_id', organizationAdminId)
        .maybeSingle();
      if (lookupError) throw lookupError;
      if (!existing) {
        throw new Error('Inventory item not found.');
      }

      const currentQuantity = Number(existing.quantity || 0);
      if (issueQty > currentQuantity) {
        throw new Error(`Only ${currentQuantity} unit(s) are available in inventory.`);
      }

      const { error: updateError } = await client
        .from('inventory_items')
        .update({
          quantity: currentQuantity - issueQty,
          updated_at: toIsoNow()
        })
        .eq('id', existing.id);
      if (updateError) throw updateError;

      const { data: inserted, error: insertError } = await client
        .from('inventory_usage_logs')
        .insert([
          {
            organization_admin_id: organizationAdminId,
            branch_id: existing.branch_id || null,
            inventory_item_id: existing.id,
            product_id: existing.product_id,
            product_name: existing.product_name,
            variation_id: existing.variation_id || null,
            variation_name: existing.variation_name || null,
            quantity: issueQty,
            note: note?.trim() || null,
            created_by: actor?.id || null,
            created_by_name: actor?.name || null,
            created_at: toIsoNow()
          }
        ])
        .select()
        .single();
      if (insertError) throw insertError;
      return mapInventoryUsageLog(inserted);
    }
  },
  payments: {
    async listTransactions(invoiceId) {
      let query = getClient()
        .from('payment_transactions')
        .select('*')
        .order('created_at', { ascending: false });
      if (invoiceId !== undefined) {
        query = query.eq('invoice_id', invoiceId);
      }
      const { data } = await query;
      return (data || []).map(
        (tx: any): PaymentTransaction => ({
          id: tx.id,
          invoiceId: tx.invoice_id,
          orderId: tx.order_id,
          amount: Number(tx.amount || 0),
          provider: tx.provider,
          gatewayTransactionId: tx.gateway_transaction_id || undefined,
          status: tx.status,
          payload: tx.payload,
          reconciledAt: tx.reconciled_at ? new Date(tx.reconciled_at) : undefined,
          createdBy: tx.created_by,
          createdAt: new Date(tx.created_at),
          updatedAt: tx.updated_at ? new Date(tx.updated_at) : undefined
        })
      );
    },
    async recordTransaction(input) {
      const { data, error } = await getClient()
        .from('payment_transactions')
        .insert([
          {
            invoice_id: input.invoiceId,
            order_id: input.orderId,
            amount: input.amount,
            provider: input.provider,
            gateway_transaction_id: input.gatewayTransactionId || null,
            status: input.status,
            payload: input.payload || null,
            created_by: input.createdBy || null
          }
        ])
        .select()
        .single();
      if (error) throw error;
      return {
        id: data.id,
        invoiceId: data.invoice_id,
        orderId: data.order_id,
        amount: Number(data.amount || 0),
        provider: data.provider,
        gatewayTransactionId: data.gateway_transaction_id || undefined,
        status: data.status,
        payload: data.payload,
        reconciledAt: data.reconciled_at ? new Date(data.reconciled_at) : undefined,
        createdBy: data.created_by,
        createdAt: new Date(data.created_at),
        updatedAt: data.updated_at ? new Date(data.updated_at) : undefined
      } as PaymentTransaction;
    },
    async reconcileInvoice(invoiceId) {
      const client = getClient();
      const { data: invoice } = await client.from('invoices').select('*').eq('id', invoiceId).single();
      const { data: txs } = await client
        .from('payment_transactions')
        .select('*')
        .eq('invoice_id', invoiceId)
        .in('status', ['SUCCESS', 'RECONCILED']);

      const reconciledAmount = (txs || []).reduce(
        (sum: number, tx: any) => sum + Number(tx.amount || 0),
        0
      );
      const invoiceAmount = getInvoicePayableAmount({
        amount: Number(invoice?.amount || 0),
        taxRate: Number(invoice?.tax_rate || 0)
      });
      const isSettled = reconciledAmount >= invoiceAmount && invoiceAmount > 0;

      if (isSettled) {
        await client.from('payment_transactions')
          .update({ status: 'RECONCILED', reconciled_at: toIsoNow(), updated_at: toIsoNow() })
          .eq('invoice_id', invoiceId)
          .in('status', ['SUCCESS', 'RECONCILED']);
        await client.from('invoices').update({ status: 'Paid' }).eq('id', invoiceId);
      }

      return { invoiceId, reconciledAmount, isSettled };
    }
  },
  async resetDatabase() { throw new Error("Manual reset required."); }
};
